// File: src/context/NotificationsProvider.js

import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
} from 'react';
import { io } from 'socket.io-client';
import { AuthContext } from './AuthContext';
import axios from '../utils/axiosGlobal';  // use your global Axios

export const NotificationsContext = createContext();

const SOCKET_URL = `${process.env.REACT_APP_API_URL}`;

// Helper to ensure notification _id is string
function normalizeNotification(notif) {
  if (!notif) return null;
  const normalized = { ...notif };
  if (normalized._id && typeof normalized._id !== 'string') {
    normalized._id = normalized._id.toString();
  }
  return normalized;
}

export function NotificationsProvider({ children }) {
  const { currentUser } = useContext(AuthContext);
  const [notifications, setNotifications] = useState([]);
  const [socket, setSocket] = useState(null);

  const unreadCount = notifications.filter((n) => !n.isRead).length;

  // fetchNotifications => uses global Axios
  const fetchNotifications = useCallback(async () => {
    if (!currentUser?._id) return;
    try {
      console.log('[NotificationsProvider] Fetching notifications...');
      const res = await axios.get(`/notifications?userId=${currentUser._id}`);
      const normalized = res.data.map(normalizeNotification);
      setNotifications(normalized);
    } catch (err) {
      console.error('[NotificationsProvider] Error fetching notifications:', err);
    }
  }, [currentUser]);

  useEffect(() => {
    // If there's no user => skip
    if (!currentUser?._id) {
      return;
    }

    // Check localStorage for an accessToken
    const storedAccessToken = localStorage.getItem('accessToken');
    if (!storedAccessToken) {
      console.log('[NotificationsProvider] No accessToken => skipping socket connection');
      return;
    }

    // 1) fetch initial notifications
    fetchNotifications();

    // 2) connect Socket.IO with the token
    const newSocket = io(SOCKET_URL, {
      transports: ['websocket'],
      reconnectionAttempts: 5,
      reconnectionDelay: 1000,
      auth: { token: storedAccessToken }, 
    });
    setSocket(newSocket);

    newSocket.on('connect', () => {
      console.log('[NotificationsProvider] Socket connected:', newSocket.id);
      newSocket.emit('joinUserRoom', currentUser._id);
    });

    newSocket.on('initialNotifications', (allNotifs) => {
      const normalized = allNotifs.map(normalizeNotification);
      setNotifications(normalized);
    });

    newSocket.on('notificationCreated', (newDoc) => {
      if (!newDoc.userId || String(newDoc.userId) !== String(currentUser._id)) return;
      const normalized = normalizeNotification(newDoc);
      setNotifications((prev) => [normalized, ...prev]);
    });

    newSocket.on('notificationRemoved', (removedId) => {
      setNotifications((prev) => {
        const idx = prev.findIndex((n) => n._id === removedId);
        if (idx !== -1) {
          const updated = [...prev];
          updated.splice(idx, 1);
          return updated;
        }
        return prev;
      });
    });

    newSocket.on('notificationUpdated', (updatedDoc) => {
      if (!updatedDoc.userId || String(updatedDoc.userId) !== String(currentUser._id)) return;
      const normalized = normalizeNotification(updatedDoc);
      setNotifications((prev) => {
        const idx = prev.findIndex((n) => n._id === normalized._id);
        if (idx !== -1) {
          const updated = [...prev];
          updated[idx] = normalized;
          return updated;
        }
        return prev;
      });
    });

    newSocket.on('disconnect', () => {
      console.log('[NotificationsProvider] Socket disconnected');
    });

    newSocket.on('error', (err) => {
      console.error('[NotificationsProvider] Socket error:', err);
    });

    // Cleanup => disconnect on unmount or user changes
    return () => {
      console.log('[NotificationsProvider] Disconnecting socket');
      newSocket.disconnect();
    };
  }, [currentUser, fetchNotifications]);

  useEffect(() => {
    console.log('[NotificationsProvider] Current notifications:', notifications);
    console.log('[NotificationsProvider] Derived unreadCount:', unreadCount);
  }, [notifications, unreadCount]);

  return (
    <NotificationsContext.Provider
      value={{ notifications, unreadCount, fetchNotifications, socket }}
    >
      {children}
    </NotificationsContext.Provider>
  );
}

export function useNotifications() {
  return useContext(NotificationsContext);
}
